<template>
  <div class="d-flex flex-column">
    <div class="grid-header">
      <h1>{{ $t("empresas_agentes.pendientes_validacion.titulo") }}</h1>
    </div>

    <DxDataGrid v-bind="getDefaultGridProperties()" :data-source="gridSource" ref="grid"
      class="dx-card wide-card grid-operaciones">
      <DxColumnFixing :enabled="true" />
      <DxColumn data-field="CodigoEstado" :caption="$t('empresas_colaboradoras.grid.estado')" cell-template="estadoTemplate">
        <DxLookup v-bind="getLookup('empresa_agente', 'estado')"></DxLookup>
      </DxColumn>
      <DxColumn data-field="Tipo" :caption="$t('operaciones.grid.tipo_empresa_agente')" cell-template="tipoEmpresaAgenteTemplate">
        <DxLookup v-bind="getLookup('empresa_agente', 'tipo')" />
      </DxColumn>
      <DxColumn data-field="Documento" :caption="$t('empresas_colaboradoras.grid.cif')"></DxColumn>
      <DxColumn data-field="RazonSocial" :caption="$t('empresas_colaboradoras.grid.razon_social')"></DxColumn>
      <DxColumn data-field="NombreCompletoRepresentante" :caption="$t('empresas_colaboradoras.grid.nombre_completo_representante')"></DxColumn>
      <DxColumn data-field="CodigoFormaJuridica" :caption="$t('empresas_colaboradoras.grid.forma_juridica')">
        <DxLookup v-bind="getLookup('empresa_agente', 'forma_juridica')"></DxLookup>
      </DxColumn>
      <DxColumn data-field="DireccionFisica" :caption="$t('empresas_colaboradoras.grid.direccion_fisica')"></DxColumn>
      <DxColumn data-field="Telefono" :caption="$t('empresas_colaboradoras.grid.telefono')"></DxColumn>
      <DxColumn data-field="Email" :caption="$t('empresas_colaboradoras.grid.email')"></DxColumn>
      <DxColumn data-field="Web" :caption="$t('empresas_colaboradoras.grid.sitio_web')"></DxColumn>
      <DxColumn type="buttons" :caption="$t('empresas_colaboradoras.grid.acciones')" :fixed="true"
        fixed-position="right" :buttons="[
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            text: $t('empresas_colaboradoras.grid.ver_detalle'),
            icon: 'fa-regular fa-magnifying-glass text-success',
            onClick: (e: any) => verDetalle(e.row?.data.Id)
          },
          {
            cssClass: 'btn btn-link text-decoration-none btn-sm',
            text: $t('empresas_colaboradoras.grid.borrar'),
            icon: 'fa-regular fa-trash text-success',
            onClick: (e: any) => borrar(e.row?.data.Id),
            disabled: (options: any) => options.row?.data.CodigoEstado != EstadoEmpresaAgentesEnum.Rechazada
          }
      ]"/>
      <template #tipoEmpresaAgenteTemplate="{ data }">
        <div :class="[`d-inline-block rounded py-1 px-2`, `tipoEmpresaAgente ${data.value.replace(' ', '')}`]">
            {{ $t(`empresa_agente.tipo.${data.value}`) }}
        </div>
      </template>
      <template #estadoTemplate="{ data }">
        <div :class="[
          `d-inline-block rounded py-1 px-2`,
          `estado-empresa-agente ${data.value}`,
        ]">
          {{ $t(`empresa_agente.estado.${data.value}`) }}
        </div>
      </template>

    </DxDataGrid>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { DxDataGrid, DxColumn, DxColumnFixing, DxLookup } from "devextreme-vue/data-grid";
import { getDefaultGridProperties, getLookup } from "@helpers/grid-helper";
import { useApi } from "@api/integration";
import type CustomStore from "devextreme/data/custom_store";
import { useIntl } from "@services/i18n";
import { useModalService } from "@services/modal-service";
import { messageBoxResult } from '@components/dialogs/message-box';
import { EstadoEmpresaAgentesEnum, type DXLoadOptions } from "@/.shared/.api";
import EmpresaAgentesPendienteValidacionDetail from "@/components/empresas-agentes/EmpresaAgentesPendienteValidacionDetail.vue";
import { toastError, toastSuccess } from "@/.shared/services/notification-service";

const intl = useIntl();
const api = useApi();
const grid = ref<DxDataGrid>();
const gridSource = ref<CustomStore>();
const modal = useModalService();

const props = defineProps({
  tipoEmpresaAgente: String
});

onMounted(async () => {
  setDataSource();
});

const setDataSource = async () => {
  gridSource.value = api.apiStore({
    key: "Id",
    load: async (p) => {
      const idsEstados = [EstadoEmpresaAgentesEnum.PdtValidar, EstadoEmpresaAgentesEnum.Rechazada, EstadoEmpresaAgentesEnum.PdtFirma];
      const q = p as DXLoadOptions;

      return await api.callStandardApi("getEmpresasAgentesForGrid", { showLoading: false }, q, idsEstados);
    }
  });
}

const verDetalle = async (idEmpresaColaboradora: string) => {
  let title = `${intl.i18n.t("empresas_colaboradoras.edicion.titulo")}`;
  modal.showModal(
    EmpresaAgentesPendienteValidacionDetail,
    { model: await api.callStandardApi("getEmpresaAgentesDetail", { showLoading: true }, idEmpresaColaboradora) },
    { title: title, width: 900, height: 650 }
  ).then((value) => {
    if (value === messageBoxResult.ok || value === messageBoxResult.no) {
      grid.value?.instance?.refresh();
    }
  });
}

const borrar = async (idEmpresaAgentes: string) => {
  let respuesta = await modal.messageBox(
    intl.i18n.t("empresas_agentes.grid.borrar_pregunta"),
    intl.i18n.t("empresas_agentes.grid.titulo_confirma_borrar"),
    "okCancel",
    "warning",
  );
  if (respuesta == messageBoxResult.ok)
  {
    try {
      await api.callStandardApi("deleteEmpresaAgentes", { showLoading: true }, idEmpresaAgentes);
      toastSuccess(intl.i18n.t("empresas_agentes.borrado_exito"));
      grid.value?.instance?.refresh();
    } catch (error) {
      toastError(intl.i18n.t("empresas_agentes.borrado_fallo"));
    }
  }
}

</script>

<style lang="scss" scoped>
</style>